import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { Bullet, Paragraph, Run, RunStyle, Spacing, TextBody as TextBodyModel } from "@deckloom/core/schema";
import { rgba } from "./fills";
import { fontStack, useFontsTick } from "./fonts";

/** Base line height PowerPoint uses for "single" spacing, as a multiple of font size. */
const SINGLE = 1.2;

export interface TextBodyProps {
  body: TextBodyModel;
  /** element id, used to tag paragraph nodes for animation targeting (`elementId#pN`) */
  elementId: string;
  /** when set, the whole body uses this color (color-change emphasis on text) */
  colorOverride?: string;
}

function spacingToLineHeight(s: Spacing, size: number, reduction: number): string {
  if (s.kind === "percent") return `${Math.max(0.5, s.value * SINGLE - reduction)}`;
  return `${s.value}px`;
}
function spacingToPx(s: Spacing, size: number): number {
  return s.kind === "percent" ? s.value * size : s.value;
}

function runStyle(r: RunStyle, fontScale: number): CSSProperties {
  const size = r.size * fontScale;
  const style: CSSProperties = {
    fontFamily: fontStack(r.font, r.fontEa),
    fontSize: `${size}px`,
    fontWeight: r.bold ? 700 : 400,
    fontStyle: r.italic ? "italic" : "normal",
    color: rgba(r.color),
    letterSpacing: r.letterSpacing ? `${r.letterSpacing}px` : undefined,
    textDecoration: [r.underline !== "none" ? "underline" : "", r.strike !== "none" ? "line-through" : ""].join(" ").trim() || undefined,
    textDecorationStyle: r.underline === "double" ? "double" : r.underline === "dotted" ? "dotted" : r.underline === "dash" ? "dashed" : r.underline === "wavy" ? "wavy" : undefined,
    fontVariant: r.caps === "small" ? "small-caps" : undefined,
    textTransform: r.caps === "all" ? "uppercase" : undefined,
    backgroundColor: r.highlight ? rgba(r.highlight) : undefined,
  };
  if (r.baseline) {
    style.verticalAlign = `${r.baseline * 100}%`;
    style.fontSize = `${size * 0.65}px`;
  }
  return style;
}

function RunView({ run, fontScale }: { run: Run; fontScale: number }) {
  if (run.kind === "break") return <br />;
  const style = runStyle(run, fontScale);
  const text = run.text;
  if (run.hyperlink?.kind === "url") {
    return (
      <a href={run.hyperlink.href} target="_blank" rel="noreferrer" style={style}>
        {text}
      </a>
    );
  }
  return <span style={style}>{text}</span>;
}

function bulletText(b: Bullet, index: number): string {
  if (b.kind === "char") return b.char;
  if (b.kind === "image") return "";
  const n = b.startAt + index;
  switch (b.scheme) {
    case "alphaLcPeriod": return `${String.fromCharCode(96 + ((n - 1) % 26) + 1)}.`;
    case "alphaUcPeriod": return `${String.fromCharCode(64 + ((n - 1) % 26) + 1)}.`;
    case "alphaLcParenR": return `${String.fromCharCode(96 + ((n - 1) % 26) + 1)})`;
    case "alphaUcParenR": return `${String.fromCharCode(64 + ((n - 1) % 26) + 1)})`;
    case "romanLcPeriod": return `${roman(n).toLowerCase()}.`;
    case "romanUcPeriod": return `${roman(n)}.`;
    case "arabicParenR": return `${n})`;
    case "arabicPlain": return `${n}`;
    default: return `${n}.`;
  }
}
function roman(n: number): string {
  const map: [number, string][] = [[1000, "M"], [900, "CM"], [500, "D"], [400, "CD"], [100, "C"], [90, "XC"], [50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]];
  let out = "";
  for (const [v, s] of map) while (n >= v) { out += s; n -= v; }
  return out;
}

function ParagraphView({ p, index, numberIndex, fontScale, reduction, elementId, mediaUrl, wrap }: {
  p: Paragraph; index: number; numberIndex: number; fontScale: number; reduction: number; elementId: string;
  mediaUrl: (id: string) => string; wrap: boolean;
}) {
  const firstStyle = (p.runs.find((r) => r.kind !== "break") as RunStyle | undefined) ?? p.endRunStyle;
  const size = (firstStyle?.size ?? 18) * fontScale;
  const bulletSize = p.bullet?.size ? spacingToPx(p.bullet.size, size) : size;
  const style: CSSProperties = {
    position: "relative",
    textAlign: p.align === "distributed" ? "justify" : p.align,
    // PowerPoint's model: wrapped lines start at marL, the first line at marL + indent, and the bullet sits at the
    // start of the first line. A hanging indent (indent < 0) leaves room for the bullet, so the text starts at marL
    // when the bullet fits; a wider bullet (or indent >= 0) pushes the text right after it.
    marginLeft: `${p.marginLeft}px`,
    marginRight: `${p.marginRight}px`,
    textIndent: `${p.indent}px`,
    marginTop: `${spacingToPx(p.spaceBefore, size)}px`,
    marginBottom: `${spacingToPx(p.spaceAfter, size)}px`,
    lineHeight: spacingToLineHeight(p.lineSpacing, size, reduction),
    fontSize: `${size}px`,
    fontFamily: firstStyle ? fontStack(firstStyle.font) : undefined,
    direction: p.rtl ? "rtl" : "ltr",
    minHeight: `${size * SINGLE}px`,
    flexShrink: 0,
    whiteSpace: wrap ? "pre-wrap" : "pre",
    overflowWrap: "break-word",
  };
  return (
    <div className="p" data-para={index} data-el={`${elementId}#p${index}`} style={style}>
      {p.bullet && (
        <span
          className="bullet"
          aria-hidden
          style={{
            display: "inline-block",
            minWidth: `${Math.max(0, -p.indent)}px`,
            textIndent: 0,
            whiteSpace: "pre",
            fontFamily: p.bullet.kind !== "image" ? fontStack(p.bullet.font) : undefined,
            fontSize: `${bulletSize}px`,
            color: p.bullet.kind !== "image" && p.bullet.color ? rgba(p.bullet.color) : firstStyle ? rgba(firstStyle.color) : undefined,
          }}
        >
          {p.bullet.kind === "image" ? (
            <img src={mediaUrl(p.bullet.mediaId)} alt="" style={{ width: `${bulletSize * 0.8}px`, height: `${bulletSize * 0.8}px` }} />
          ) : (
            bulletText(p.bullet, numberIndex)
          )}
        </span>
      )}
      {p.runs.length === 0 ? "​" : p.runs.map((r, i) => <RunView key={i} run={r} fontScale={fontScale} />)}
    </div>
  );
}

const MIN_SHRINK = 0.25;

export function TextBodyView({ body, elementId, colorOverride, mediaUrl }: TextBodyProps & { mediaUrl: (id: string) => string }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  // "Shrink text on overflow": PowerPoint stores the result as fontScale, but files written by other tools (or
  // edited text) may lack it, so the renderer also shrinks until the paragraphs fit the box.
  const [shrink, setShrink] = useState(1);
  const iterations = useRef(0);
  const fontsTick = useFontsTick();
  const autofit = body.autofit.kind === "normal";
  useLayoutEffect(() => {
    if (!autofit) return;
    const root = rootRef.current, inner = innerRef.current;
    if (!root || !inner) return;
    const avail = root.clientHeight - body.insets.t - body.insets.b;
    const need = inner.offsetHeight;
    if (avail <= 0 || need <= avail + 0.5 || shrink <= MIN_SHRINK || iterations.current >= 8) return;
    iterations.current++;
    setShrink((s) => Math.max(MIN_SHRINK, s * Math.max(0.6, Math.min(0.97, avail / need))));
  });
  useLayoutEffect(() => { iterations.current = 0; setShrink(1); }, [elementId, fontsTick]);

  const fontScale = (body.autofit.kind === "normal" ? body.autofit.fontScale : 1) * shrink;
  const reduction = (body.autofit.kind === "normal" ? body.autofit.lineSpacingReduction : 0) + Math.min(0.2, 1 - shrink);
  const justify = body.anchor === "middle" ? "center" : body.anchor === "bottom" ? "flex-end" : "flex-start";
  const writing = body.vertical === "eaVert" ? "vertical-rl" : body.vertical === "vert" ? "vertical-rl" : body.vertical === "vert270" ? "vertical-lr" : undefined;
  const style: CSSProperties = {
    boxSizing: "border-box",
    width: "100%",
    height: "100%",
    padding: `${body.insets.t}px ${body.insets.r}px ${body.insets.b}px ${body.insets.l}px`,
    display: "flex",
    flexDirection: "column",
    justifyContent: justify,
    alignItems: body.anchorCenter ? "center" : "stretch",
    writingMode: writing,
    whiteSpace: body.wrap ? "pre-wrap" : "pre",
    overflow: "visible",
    columnCount: body.columns?.count,
    columnGap: body.columns ? `${body.columns.gap}px` : undefined,
    color: colorOverride,
  };
  // Numbered bullets count per level: nested items do not break the parent's sequence, but a paragraph at the
  // same or a shallower level without a number does.
  const counters: number[] = [];
  return (
    <div className="tb" style={style} ref={rootRef}>
      <div className="tb-inner" ref={innerRef} style={{ flexShrink: 0 }}>
      {body.paragraphs.map((p, i) => {
        let counter = 0;
        if (p.bullet?.kind === "number") {
          counter = (counters[p.level] ?? 0) + 1;
          counters[p.level] = counter;
          counters.length = p.level + 1;
        } else if (p.runs.some((r) => r.kind !== "break" && r.text.trim())) {
          counters.length = p.level;
        }
        return (
          <ParagraphView key={p.id ?? i} p={p} index={i} numberIndex={counter - 1} fontScale={fontScale} reduction={reduction} elementId={elementId} mediaUrl={mediaUrl} wrap={body.wrap} />
        );
      })}
      </div>
    </div>
  );
}
